/**
 * Utilitários para verificar roles do usuário a partir do JWT do Keycloak.
 * Pode ser usado em qualquer lugar do app (componentes, hooks, páginas).
 *
 * REGRA ÚNICA DE TIPO DE USUÁRIO (alterar só aqui):
 * - iss termina com /realms/musa → interno (permission_role: 'internal')
 * - iss termina com /realms/generator + role admin → admin (permission_role: 'admin')
 * - iss termina com /realms/generator + role standard (ou outro) → standard (permission_role: 'standard')
 */

import { CLIENT_ID } from '@/config/auth';

const TOKEN_STORAGE_KEY = 'keycloak_tokens';

/** Tipo de permissão do usuário no app (derivado do iss + role do token). */
export type PermissionRole = 'internal' | 'admin' | 'standard';

/** Resultado da regra única: tipo de usuário a partir do token. */
export interface TokenBasedUserType {
  isInternal: boolean;
  permission_role: PermissionRole;
  isAdmin: boolean;
}

/** Vínculo de um generator no JWT (claim customizado). */
export interface JwtGeneratorLink {
  id: string;
  include_children: boolean;
  /**
   * O papel do usuário passou a vir do claim `groups` (GR-637). Este campo
   * continua sendo escrito em paralelo pela musa-generator-api e é lido como
   * fallback — opcional porque some quando o attribute for aposentado, depois
   * que o último consumidor migrar (GR-561, [BREAKING]).
   */
  role?: string;
}

interface KeycloakTokenPayload {
  /** Issuer do JWT (ex.: https://keycloak.stg.musa.co/realms/musa ou .../realms/generator) */
  iss?: string;
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  role?: string;
  user_role?: string;
  /** Generator ID da nova engine (claim customizado) - pode vir como string ou number */
  generator_id?: number | string;
  /** Generator ID alternativo (claim customizado) - pode vir como string ou number */
  generator?: number | string;
  /** Lista de vínculos de generators do usuário (claim customizado). */
  generators?: JwtGeneratorLink[];
  /**
   * Perfis de acesso do catálogo GR-637 — membership em Keycloak Groups,
   * publicada pelo mapper `groups` do client `portal-gerador`. Fonte de
   * verdade do permissionamento a partir da Onda 0.
   */
  groups?: string[];
  [key: string]: unknown;
}

/** Verifica se o issuer do token é do realm Musa (usuários internos). */
function isIssuerRealmMusa(iss: string | undefined): boolean {
  if (!iss || typeof iss !== 'string') return false;
  const normalized = iss.toLowerCase().replace(/\/$/, '');
  return normalized.endsWith('/realms/musa');
}

/** Verifica se o issuer do token é do realm Generator (geradores). */
function isIssuerRealmGenerator(iss: string | undefined): boolean {
  if (!iss || typeof iss !== 'string') return false;
  const normalized = iss.toLowerCase().replace(/\/$/, '');
  return normalized.endsWith('/realms/generator');
}

/** Extrai a role "bruta" do payload (admin, standard, internal, user, etc.). */
function getRawRoleFromPayload(payload: KeycloakTokenPayload): string | null {
  // `role` vazio é resposta definitiva ("não tem papel no attribute"), NÃO
  // convite para procurar em `realm_access.roles` — cair no fallback ali
  // promoveria qualquer um que tivesse uma realm role chamada `admin`. Hoje a
  // musa-generator-api só escreve vazio para usuário sem vínculo de gerador,
  // mas isso vale para qualquer origem do claim.
  if (typeof payload.role === 'string') return payload.role || null;
  if (typeof payload.user_role === 'string') return payload.user_role;
  const realmRoles = payload.realm_access?.roles ?? [];
  const order = ['admin', 'internal', 'standard', 'user'];
  for (const r of order) {
    if (realmRoles.includes(r)) return r;
  }
  return realmRoles[0] ?? null;
}

/**
 * REGRA ÚNICA: determina o tipo de usuário a partir do token (iss + role top-level).
 * - Realm Musa → interno
 * - Realm Generator + role top-level "admin" → admin
 * - Realm Generator + role top-level standard (ou outro) → standard
 *
 * IMPORTANTE: o role top-level reflete o gerador primário (generator_id).
 * Para permissões por gerador específico, use getRoleForGenerator().
 */
export function getTokenBasedUserType(): TokenBasedUserType | null {
  const token = getStoredAccessToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const iss = payload.iss;
  const rawRole = getRawRoleFromPayload(payload) ?? 'standard';

  if (isIssuerRealmMusa(iss)) {
    return { isInternal: true, permission_role: 'internal', isAdmin: false };
  }

  if (isIssuerRealmGenerator(iss)) {
    // Grupo primeiro, `role` como fallback (GR-637). O attribute continua
    // sendo escrito em paralelo, então hoje os dois respondem: o grupo para
    // quem já foi migrado, o attribute para todo mundo. Ler os dois é o que
    // permite subir antes da migração e reverter o Portal sem ninguém perder
    // acesso.
    const isAdmin =
      (payload.groups !== undefined && hasAdminProfileInToken()) ||
      rawRole === 'admin';
    return {
      isInternal: false,
      permission_role: isAdmin ? 'admin' : 'standard',
      isAdmin,
    };
  }

  // Fallback (iss desconhecido): tratar como standard
  return {
    isInternal: false,
    permission_role: 'standard',
    isAdmin: false,
  };
}

/**
 * Perfis de acesso (Keycloak Groups) do usuário atual, vindos do claim
 * `groups` — catálogo GR-637. Vazio para quem ainda não foi migrado.
 */
export function getProfilesFromToken(): string[] {
  const token = getStoredAccessToken();
  if (!token) return [];

  const payload = decodeJwtPayload(token);
  if (!payload) return [];

  const groups = payload.groups;
  if (!Array.isArray(groups)) return [];
  // `full_path = false` no mapper já entrega sem barra; o replace cobre um
  // token emitido antes dessa configuração.
  return groups
    .filter((g): g is string => typeof g === 'string')
    .map((g) => g.replace(/^\//, ''));
}

/** Perfil que concede a gestão de usuários — espelha ADMIN_PROFILE_KEY. */
const ADMIN_GROUP = 'admin';

/**
 * `true` quando o usuário tem o perfil `admin` no Keycloak.
 *
 * Esta é a fonte de verdade nova. O claim `role` continua sendo lido como
 * fallback em `getRawRoleFromPayload` enquanto a migração dos usuários
 * existentes para os grupos não roda (GR-561, regra 16: bloqueio só entra em
 * vigor depois da conferência da migração).
 */
export function hasAdminProfileInToken(): boolean {
  return getProfilesFromToken().includes(ADMIN_GROUP);
}

/**
 * Permissões efetivas do usuário atual — as client roles de `portal-gerador`
 * que o Keycloak publica em `resource_access`, concedidas pela membership nos
 * grupos do catálogo GR-637.
 *
 * Leitura crua, sem regra de negócio: NÃO aplica o acesso total do usuário
 * interno (GR-561, regra 10) nem qualquer fallback de legado. Quem decide isso
 * é `usePermissions`, onde o tipo do usuário é conhecido.
 *
 * Vazio significa uma de duas coisas, indistinguíveis aqui: o usuário não tem
 * nenhuma role, ou ainda não foi migrado para os grupos. Use
 * `isMigratedToProfiles()` para separar os dois casos antes de negar acesso.
 */
export function getPermissionsFromToken(): string[] {
  const token = getStoredAccessToken();
  if (!token) return [];

  const payload = decodeJwtPayload(token);
  if (!payload) return [];

  const roles = payload.resource_access?.[CLIENT_ID]?.roles;
  if (!Array.isArray(roles)) return [];

  return roles.filter((r): r is string => typeof r === 'string');
}

/** `true` quando a permissão está no token. Sem bypass de interno — ver `usePermissions`. */
export function hasPermissionInToken(permission: string): boolean {
  return getPermissionsFromToken().includes(permission);
}

/** Client do MusaHub no realm musa: é nele que moram as roles de caçamba. */
const HUB_CLIENT_ID = 'musa_cadastros';

/**
 * Roles do Hub que agendam caçamba. `bucket_create` é o equivalente da transição, aceito pelo
 * musa-agreements até `bucket_schedule` estar distribuída (`canScheduleBuckets`).
 */
const HUB_BUCKET_SCHEDULE_ROLES = ['bucket_schedule', 'bucket_create'];

/**
 * `true` quando o token é do realm musa e traz uma role do Hub que agenda caçamba.
 *
 * O token do portal carrega as roles do Hub porque os clients do realm musa têm escopo completo.
 * O realm entra na conta porque o musa-agreements também confere: a tela não pode oferecer o que
 * o serviço recusaria.
 */
export function canScheduleBucketsInToken(): boolean {
  const token = getStoredAccessToken();
  if (!token) return false;

  const payload = decodeJwtPayload(token);
  if (!payload || !isIssuerRealmMusa(payload.iss)) return false;

  const roles = payload.resource_access?.[HUB_CLIENT_ID]?.roles;
  return Array.isArray(roles) && roles.some((role) => HUB_BUCKET_SCHEDULE_ROLES.includes(role));
}

/**
 * `true` quando o usuário já pertence a algum grupo do catálogo GR-637.
 *
 * Todo usuário migrado cai em pelo menos `default`, então grupo nenhum = ainda
 * não migrado. É a diferença entre "não pode" e "ainda não sabemos", e é o que
 * segura o bloqueio na Onda 1 (GR-561, regra 16: bloquear só depois da
 * conferência da migração).
 */
export function isMigratedToProfiles(): boolean {
  return getProfilesFromToken().length > 0;
}

/**
 * Retorna o array de generators do JWT do usuário atual.
 * Cada item contém id e include_children por generator (e `role` para quem
 * ainda tem o attribute legado).
 */
export function getGeneratorsFromToken(): JwtGeneratorLink[] {
  const token = getStoredAccessToken();
  if (!token) return [];

  const payload = decodeJwtPayload(token);
  if (!payload) return [];

  return payload.generators ?? [];
}

/**
 * Retorna o vínculo de um generator específico para o usuário atual.
 * Retorna null se o usuário não tiver acesso a esse generator.
 */
export function getGeneratorLink(generatorId: string | number): JwtGeneratorLink | null {
  const id = String(generatorId);
  return getGeneratorsFromToken().find((g) => g.id === id) ?? null;
}

/**
 * Retorna o role do usuário para um generator específico.
 * Retorna null se o usuário não tiver acesso a esse generator.
 */
export function getRoleForGenerator(generatorId: string | number): string | null {
  return getGeneratorLink(generatorId)?.role ?? null;
}

/**
 * Verifica se o usuário tem role "admin" no generator especificado.
 * Usuários internos sempre retornam true.
 */
export function isAdminForGenerator(generatorId: string | number): boolean {
  if (isInternal()) return true;
  return getRoleForGenerator(generatorId) === 'admin';
}

/**
 * Verifica se o usuário tem acesso a todos os níveis filhos do generator.
 * Quando false, o acesso é restrito às unidades selecionadas manualmente.
 */
export function includesChildrenForGenerator(generatorId: string | number): boolean {
  return getGeneratorLink(generatorId)?.include_children ?? false;
}

/**
 * Verifica se o usuário tem acesso (qualquer role) ao generator especificado.
 * Usuários internos sempre retornam true.
 */
export function hasAccessToGenerator(generatorId: string | number): boolean {
  if (isInternal()) return true;
  return getGeneratorLink(generatorId) !== null;
}

/**
 * Decodifica o payload de um JWT (parte do meio, base64url).
 * Não valida assinatura (a API valida); apenas lê claims no front.
 */
export function decodeJwtPayload(token: string): KeycloakTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadBase64 = parts[1];
    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    const json = atob(padded);
    return JSON.parse(json) as KeycloakTokenPayload;
  } catch {
    return null;
  }
}

/**
 * Retorna o access token armazenado (sessionStorage) ou null.
 */
export function getStoredAccessToken(): string | null {
  try {
    const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored) as { accessToken?: string };
    return data.accessToken ?? null;
  } catch {
    return null;
  }
}

/**
 * Verifica se o usuário atual é admin (realm Generator + role admin).
 * Usa a regra única: iss + role.
 */
export function isAdmin(): boolean {
  return getTokenBasedUserType()?.isAdmin ?? false;
}

/**
 * Verifica se o usuário atual é interno (realm Musa).
 * Usa a regra única: iss termina com /realms/musa.
 */
export function isInternal(): boolean {
  return getTokenBasedUserType()?.isInternal ?? false;
}

/**
 * Retorna a permission_role do usuário no app: 'internal' | 'admin' | 'standard'.
 * Derivada da regra única (iss + role), não do claim bruto do token.
 */
export function getCurrentUserRole(): PermissionRole | null {
  return getTokenBasedUserType()?.permission_role ?? null;
}

/**
 * Retorna o generator_id do usuário atual a partir do token JWT.
 * Procura pelos claims "generator_id" ou "generator" (customizados no Keycloak).
 * 
 * @returns O ID do generator na nova engine ou null se não disponível
 */
export function getCurrentUserGeneratorId(): number | null {
  const token = getStoredAccessToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  // Tentar obter generator_id de claims customizados (pode vir como number ou string)
  if (payload.generator_id) {
    const parsed = typeof payload.generator_id === 'number' 
      ? payload.generator_id 
      : parseInt(payload.generator_id as string, 10);
    if (!isNaN(parsed)) return parsed;
  }
  
  if (payload.generator) {
    const parsed = typeof payload.generator === 'number'
      ? payload.generator
      : parseInt(payload.generator as string, 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Fallback: derivar do primeiro vínculo em generators[] quando claims legados estiverem ausentes
  const firstLink = payload.generators?.[0];
  if (firstLink?.id) {
    const parsed = parseInt(firstLink.id, 10);
    if (!isNaN(parsed)) return parsed;
  }

  return null;
}

/**
 * Retorna informações básicas do usuário atual extraídas do token JWT.
 * isInternal, isAdmin e role vêm da regra única (getTokenBasedUserType).
 */
export function getCurrentUserInfo(): {
  sub: string | null;
  email: string | null;
  name: string | null;
  role: PermissionRole | null;
  generatorId: number | null;
  generators: JwtGeneratorLink[];
  isInternal: boolean;
  isAdmin: boolean;
} | null {
  const token = getStoredAccessToken();
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const userType = getTokenBasedUserType();
  if (!userType) return null;

  const generatorId = getCurrentUserGeneratorId();

  return {
    sub: payload.sub ?? null,
    email: payload.email ?? null,
    name: payload.name ?? payload.preferred_username ?? null,
    role: userType.permission_role,
    generatorId,
    generators: payload.generators ?? [],
    isInternal: userType.isInternal,
    isAdmin: userType.isAdmin,
  };
}

/**
 * Verifica se o usuário atual pode gerenciar outros usuários.
 * Apenas admins e internos têm essa permissão.
 */
export function canManageUsers(): boolean {
  return isAdmin() || isInternal();
}

/**
 * Verifica se o usuário atual pode ver todos os generators.
 * Apenas usuários internos têm essa permissão.
 */
export function canViewAllGenerators(): boolean {
  return isInternal();
}
